import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useSearchSuggestions } from "@/hooks/useSearch";
import { AdvancedSearchDialog } from "../search/AdvancedSearchDialog";
import { Search, Book, Brain, History, Atom, Calculator, Heart, Settings, Scale } from "lucide-react";

interface SearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SearchCommand = ({ open, onOpenChange }: SearchCommandProps) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { data: suggestions } = useSearchSuggestions(searchQuery);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  const handleSearch = (query: string) => {
    onOpenChange(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const runCommand = (href: string) => {
    onOpenChange(false);
    navigate(href);
  };

  const disciplineItems = [
    { title: "Theology & Church History", href: "/study/theology-church-history", icon: Book },
    { title: "Philosophy", href: "/study/philosophy", icon: Brain },
    { title: "History", href: "/study/history", icon: History },
    { title: "Law", href: "/study/law", icon: Scale },
    { title: "Mathematics", href: "/study/mathematics", icon: Calculator },
    { title: "Natural Sciences", href: "/study/natural-sciences", icon: Atom },
    { title: "Engineering & Technology", href: "/study/engineering", icon: Brain },
    { title: "Health Sciences", href: "/study/health-sciences", icon: Heart },
  ];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Search knowledge domains..." 
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>
          <div className="py-6 text-center space-y-2">
            <p>No results found.</p>
            <AdvancedSearchDialog>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Advanced Search
              </Button>
            </AdvancedSearchDialog>
          </div>
        </CommandEmpty>

        {/* Search Actions */}
        {searchQuery && (
          <CommandGroup heading="Search Actions">
            <CommandItem onSelect={() => handleSearch(searchQuery)}>
              <Search className="h-4 w-4 mr-2" />
              Search for "{searchQuery}"
            </CommandItem>
            <CommandItem onSelect={() => onOpenChange(false)}>
              <AdvancedSearchDialog>
                <div className="flex items-center w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Advanced Search
                </div>
              </AdvancedSearchDialog>
            </CommandItem>
          </CommandGroup>
        )}

        {/* Search Suggestions */}
        {suggestions && suggestions.length > 0 && (
          <CommandGroup heading="Suggestions">
            {suggestions.map((suggestion, index) => (
              <CommandItem
                key={index}
                onSelect={() => handleSearch(suggestion.suggestion)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Search className="h-4 w-4" />
                <span>{suggestion.suggestion}</span>
                <span className="text-xs text-black ml-auto">
                  {suggestion.type}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Quick Navigation */}
        <CommandGroup heading="Disciplines">
          {disciplineItems.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.href}
                onSelect={() => runCommand(item.href)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Icon className="h-4 w-4" />
                <span>{item.title}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
